import {
	type EntityDefinition,
	type FieldDefinition,
	liveFields,
} from "@/shared/contracts/structure";
import { panelContext } from "@/shared/format/context";
import { formatValue, type ReferenceLabels, referenceEntityOf, referenceKey } from "./field-config";

/** Uma data curta no fuso do painel — uma data sem hora não muda de dia com o fuso. */
export function shortDate(value: string, withTime = false): string | null {
	const parsed = new Date(value);
	if (Number.isNaN(parsed.getTime())) return null;
	const ctx = panelContext();
	return new Intl.DateTimeFormat(ctx.locale, {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
		...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}),
		timeZone: value.includes("T") ? ctx.timeZone : "UTC",
	}).format(parsed);
}

/**
 * O NOME DECLARADO (`entity.labelFields`, 02/11) ganha de toda a regra abaixo:
 * dois horários fixos da mesma terapeuta e do mesmo serviço só se distinguem
 * pelo dia e pela hora, que a regra automática não escolhe (E2E psicologia).
 * Cada campo pelo valor legível, " · "; um vínculo pelo nome já resolvido
 * (`labels`), e sem nome fica de fora — um uuid não é nome. `null` sem
 * declaração ou sem nada preenchido: vale a regra automática.
 */
export function declaredName(
	entity: EntityDefinition,
	row: Record<string, unknown>,
	labels?: ReferenceLabels,
): string | null {
	const fields = liveFields(entity);
	const parts = (entity.labelFields ?? []).flatMap((code) => {
		const f = fields.find((x) => x.code === code);
		const value = row[code];
		if (!f || value === null || value === undefined || value === "") return [];
		const target = referenceEntityOf(f.type, f.config);
		if (!target) return [formatValue(value, f.type, panelContext(), f.config)];
		const name = labels?.get(referenceKey(target, String(value)))?.split(" · ")[0];
		return name ? [name] : [];
	});
	return parts.length > 0 ? parts.join(" · ") : null;
}

/**
 * O VÍNCULO que qualifica uma linha sem título: o primeiro preenchido. Um membro
 * da equipe (`ref_user`) só quando a linha não tem texto próprio — quem atendeu
 * não é de quem a linha é: "#005 · Balcão" ganhava "· Benvox QA", o garçom de
 * `$me`, e todo pedido parecia do garçom (E2E restaurante, 01/11).
 */
export function qualifierField(
	entity: EntityDefinition,
	row: Record<string, unknown>,
): FieldDefinition | undefined {
	const fields = liveFields(entity);
	const titled = fields.some((f) => f.type === "text" && filledText(row[f.code]));
	return fields.find(
		(f) =>
			referenceEntityOf(f.type, f.config) !== "" &&
			(f.type !== "ref_user" || !titled) &&
			filledText(row[f.code]),
	);
}

const filledText = (value: unknown): value is string =>
	typeof value === "string" && value.trim().length > 0;

/**
 * O nome COMPOSTO de uma linha sem campo-título: os dois primeiros pedaços
 * presentes, nesta ordem — o primeiro TEXTO preenchido (placa, modelo, assunto;
 * obrigatório ou não, na ordem dos campos), de QUEM é (`refName`, o vínculo já
 * resolvido a um salto), de QUANDO é (a primeira data). "ABC-1234 · Jorge",
 * "Jorge · 01/09/2026", "Revisão · 01/09/2026".
 *
 * Dois e não três: o terceiro pedaço já não ajuda a achar a linha num seletor,
 * só a empurra para fora da largura. O servidor segue a mesma ordem sem o salto
 * (`linkLabelOrNull`, `contexts/structure/domain/link-choice.ts`).
 *
 * Mora em `dynamic/` e não em `features/records` (30/09): o card do quadro
 * mostrava um pedaço do uuid ("5fcd8cbf") para a mesma linha que a lista
 * nomeava "Maria · 01/10/2026" — uma feature não importa de outra, e o rótulo
 * de um registo é de todas as telas.
 */
export function composedName(
	entity: EntityDefinition,
	row: Record<string, unknown>,
	refName: string | null,
): string | null {
	const declared = declaredName(entity, row);
	if (declared) return declared;
	const filled = liveFields(entity).filter((f) => {
		const value = row[f.code];
		return typeof value === "string" && value.trim().length > 0;
	});
	const text = filled.find((f) => f.type === "text");
	const dated = filled.find((f) => f.type === "date" || f.type === "datetime");
	const parts = [
		text ? String(row[text.code]).trim() : null,
		// O rótulo de um contato vem como "Nome · +55…": no nome composto basta o nome.
		refName?.split(" · ")[0] ?? null,
		// Data e hora num agendamento: dois no mesmo dia só se distinguem pela hora.
		dated ? shortDate(String(row[dated.code]), dated.type === "datetime") : null,
	].filter((part): part is string => Boolean(part));
	return parts.length > 0 ? parts.slice(0, 2).join(" · ") : null;
}

/**
 * O nome de uma linha cuja primeira coluna (o título) está VAZIA — a identidade
 * composta com o nome do primeiro vínculo preenchido que a tela já resolveu.
 * Tabela e cartões do telefone mostram isto no lugar do "—".
 */
export function composedTitle(
	entity: EntityDefinition,
	row: Record<string, unknown>,
	labels: ReferenceLabels,
): string | null {
	const declared = declaredName(entity, row, labels);
	if (declared) return declared;
	const ref = qualifierField(entity, row);
	const refName = ref
		? (labels.get(referenceKey(referenceEntityOf(ref.type, ref.config), String(row[ref.code]))) ??
			null)
		: null;
	return composedName(entity, row, refName);
}

/**
 * O NOME LEGÍVEL de um registro, para títulos (drawer, painel do cartão, evento
 * da agenda): o texto OBRIGATÓRIO quando há — a mesma ordem de `labelForRow` no
 * seletor —, senão a identidade composta. `null` quando a linha não tem nada
 * que a nomeie, e quem chama diz o "sem título" na língua dele.
 */
export function recordName(
	entity: EntityDefinition,
	row: Record<string, unknown>,
	labels: ReferenceLabels,
): string | null {
	const declared = declaredName(entity, row, labels);
	if (declared) return declared;
	const titled = liveFields(entity).find((f) => f.type === "text" && f.required);
	const title = titled ? row[titled.code] : null;
	if (typeof title === "string" && title.trim().length > 0) return title.trim();
	return composedTitle(entity, row, labels);
}
